import { useState } from 'react'
import { KlearDate } from '@klear/date'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Modal } from '@klear/ui/components/modal'
import { Textarea } from '@klear/ui/components/textarea'

export type PayableMarkPaidFormValues = {
  paymentDate: string
  externalReference?: string
  notes?: string
}

type PayableMarkPaidModalProps = {
  open: boolean
  isLoading?: boolean
  onClose: () => void
  onConfirm: (values: PayableMarkPaidFormValues) => void | Promise<void>
}

export function PayableMarkPaidModal({ open, isLoading = false, onClose, onConfirm }: PayableMarkPaidModalProps) {
  const [externalReference, setExternalReference] = useState('')
  const [paymentDate, setPaymentDate] = useState<KlearDate>(() => KlearDate.today())
  const [notes, setNotes] = useState('')

  const reset = () => {
    setExternalReference('')
    setPaymentDate(KlearDate.today())
    setNotes('')
  }

  return (
    <Modal
      size="lg"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isLoading) {
          reset()
          onClose()
        }
      }}
      title="Mark as Paid?"
      description="Record that this payable was paid outside of Klear (wire, check, ACH, etc.)."
      primaryText={isLoading ? 'Processing...' : 'Mark as Paid'}
      primaryDisabled={isLoading}
      secondaryText="Cancel"
      secondaryDisabled={isLoading}
      handleSecondaryAction={() => {
        if (isLoading) return
        reset()
        onClose()
      }}
      handlePrimaryAction={() => {
        void onConfirm({
          paymentDate: paymentDate.toJSON(),
          externalReference: externalReference || undefined,
          notes: notes || undefined
        })
      }}
    >
      <div className="space-y-4">
        <div>
          <Label>External Reference</Label>
          <Input
            placeholder="Transaction ID, wire ref, check number..."
            value={externalReference}
            onChange={(e) => setExternalReference(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optional. Used to track this payment in the external system.
          </p>
        </div>
        <div>
          <Label>Payment Date</Label>
          <DatePicker
            value={paymentDate}
            onChange={(d) => {
              if (d) setPaymentDate(d)
            }}
            isEditing={true}
          />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea
            placeholder="Additional details..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </Modal>
  )
}
